import { useCallback, useEffect, useRef, useState } from "react";
import { sendGtagEvent } from "@/lib/client/gtag";

interface UseCopyToClipboardOptions {
  /** Callback fired after successful copy */
  onCopy?: () => void;
  /** Delay before resetting copied state (ms) */
  resetDelay?: number;
  /** Google Analytics event category */
  eventCategory?: string;
  /** Google Analytics event action */
  eventAction?: string;
  /** Google Analytics event label */
  eventLabel?: string;
}

/**
 * Hook for copying text to clipboard with automatic state reset.
 *
 * Uses the Clipboard API and tracks events with Google Analytics.
 * Automatically resets the "copied" state after a delay.
 *
 * @param options - Configuration options for copy behavior and analytics
 * @returns Object containing:
 * - copied: Boolean indicating if text was recently copied
 * - copyToClipboard: Function to copy text to clipboard
 *
 * @example
 * ```tsx
 * function ShareButton({ url }) {
 *   const { copied, copyToClipboard } = useCopyToClipboard({
 *     onCopy: () => toast.success("Link copied!"),
 *     resetDelay: 2000,
 *     eventCategory: "share",
 *     eventAction: "copy_link",
 *   });
 *
 *   return (
 *     <button onClick={() => copyToClipboard(url)}>
 *       {copied ? "Copied!" : "Copy link"}
 *     </button>
 *   );
 * }
 * ```
 */
export const useCopyToClipboard = (options: UseCopyToClipboardOptions = {}) => {
  const {
    onCopy,
    resetDelay = 500,
    eventCategory = "search",
    eventAction = "click",
    eventLabel = "share_link",
  } = options;

  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    },
    []
  );

  const copyToClipboard = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        sendGtagEvent(eventAction, eventCategory, eventLabel);
        onCopy?.();

        if (resetTimerRef.current) {
          clearTimeout(resetTimerRef.current);
        }

        resetTimerRef.current = setTimeout(() => {
          resetTimerRef.current = null;
          setCopied(false);
        }, resetDelay);
      } catch (error) {
        console.error("Failed to copy to clipboard:", error);
      }
    },
    [onCopy, resetDelay, eventCategory, eventAction, eventLabel]
  );

  return { copied, copyToClipboard };
};
